'use client';

import { useState } from 'react';

export default function Home() {
  const [image, setImage] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
          setImage(event.target?.result as string);
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReset = () => {
    setImage(null);
    setImageDimensions({ width: 0, height: 0 });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <div className="container mx-auto px-4 py-8">
        <header className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">
            图片裁剪工具
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            上传图片，精确选择裁切区域
          </p>
        </header>

        {!image ? (
          <UploadSection onImageUpload={handleImageUpload} />
        ) : (
          <CropSection
            image={image}
            imageDimensions={imageDimensions}
            onReset={handleReset}
          />
        )}
      </div>
    </div>
  );
}

function UploadSection({ onImageUpload }: { onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-md">
        <label
          htmlFor="image-upload"
          className="group flex aspect-square cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-white transition-all hover:border-blue-500 hover:bg-blue-50 dark:border-slate-600 dark:bg-slate-800 dark:hover:border-blue-400 dark:hover:bg-slate-700"
        >
          <svg
            className="mb-4 h-16 w-16 text-slate-400 transition-colors group-hover:text-blue-500 dark:text-slate-500 dark:group-hover:text-blue-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-center text-lg font-medium text-slate-600 dark:text-slate-300">
            点击或拖拽图片到此处
          </p>
          <p className="mt-2 text-sm text-slate-400 dark:text-slate-500">
            支持 PNG、JPG、GIF 等格式
          </p>
        </label>
        <input
          id="image-upload"
          type="file"
          accept="image/*"
          onChange={onImageUpload}
          className="hidden"
        />
      </div>
    </div>
  );
}

function CropSection({
  image,
  imageDimensions,
  onReset,
}: {
  image: string;
  imageDimensions: { width: number; height: number };
  onReset: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={onReset}
          className="rounded-lg bg-slate-600 px-4 py-2 text-white transition-colors hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600"
        >
          重新上传
        </button>
      </div>
      <ImageCropper image={image} imageDimensions={imageDimensions} />
    </div>
  );
}

function ImageCropper({
  image,
  imageDimensions,
}: {
  image: string;
  imageDimensions: { width: number; height: number };
}) {
  const [point1, setPoint1] = useState<{ x: number; y: number } | null>(null);
  const [point2, setPoint2] = useState<{ x: number; y: number } | null>(null);
  const [displayedImageSize, setDisplayedImageSize] = useState({ width: 0, height: 0 });

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(e.clientX - rect.left);
    const y = Math.round(e.clientY - rect.top);

    if (!point1) {
      setPoint1({ x, y });
    } else if (!point2) {
      setPoint2({ x, y });
    } else {
      setPoint1({ x, y });
      setPoint2(null);
    }
  };

  const handleReset = () => {
    setPoint1(null);
    setPoint2(null);
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const rect = img.getBoundingClientRect();
    setDisplayedImageSize({ width: rect.width, height: rect.height });
  };

  const getPixelsCoordinates = (displayPoint: { x: number; y: number }) => {
    if (displayedImageSize.width === 0 || displayedImageSize.height === 0) {
      return { x: 0, y: 0 };
    }
    const scaleX = imageDimensions.width / displayedImageSize.width;
    const scaleY = imageDimensions.height / displayedImageSize.height;
    return {
      x: Math.round(displayPoint.x * scaleX),
      y: Math.round(displayPoint.y * scaleY),
    };
  };

  const getCropArea = () => {
    if (!point1 || !point2) return null;

    const p1 = getPixelsCoordinates(point1);
    const p2 = getPixelsCoordinates(point2);

    const x = Math.min(p1.x, p2.x);
    const y = Math.min(p1.y, p2.y);
    const width = Math.abs(p2.x - p1.x);
    const height = Math.abs(p2.y - p1.y);

    return { x, y, width, height };
  };

  const handleSaveAndDownload = () => {
    const cropArea = getCropArea();
    if (!cropArea) return;

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(
          img,
          cropArea.x,
          cropArea.y,
          cropArea.width,
          cropArea.height,
          0,
          0,
          cropArea.width,
          cropArea.height
        );
        const link = document.createElement('a');
        link.download = `cropped_${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    };
    img.src = image;
  };

  const cropArea = getCropArea();
  const pixelArea = cropArea ? cropArea.width * cropArea.height : 0;

  const formatArea = (area: number): string => {
    if (area >= 1000000) {
      return `${(area / 1000000).toFixed(3)} Mpx`;
    } else if (area >= 1000) {
      return `${(area / 1000).toFixed(3)} Kpx`;
    } else {
      return `${area.toLocaleString()} px²`;
    }
  };

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-lg bg-white shadow-lg dark:bg-slate-800">
        <div className="flex">
          {/* 上方标尺 */}
          <div className="flex flex-shrink-0">
            <div className="h-8 w-12 border-b border-r border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700" />
            <HorizontalRuler
              width={displayedImageSize.width}
              totalPixels={imageDimensions.width}
            />
          </div>
        </div>
        <div className="flex">
          {/* 左侧标尺 */}
          <VerticalRuler
            height={displayedImageSize.height}
            totalPixels={imageDimensions.height}
          />
          {/* 图片区域 */}
          <div className="relative" style={{ width: displayedImageSize.width || 'auto', height: displayedImageSize.height || 'auto' }}>
            <img
              src={image}
              alt="To crop"
              className="cursor-crosshair"
              onClick={handleImageClick}
              onLoad={handleImageLoad}
              useMap="#image-map"
              style={{ display: 'block' }}
            />
            {/* 标记点 */}
            {point1 && (
              <MarkerPoint x={point1.x} y={point1.y} label="1" color="#3b82f6" />
            )}
            {point2 && (
              <MarkerPoint x={point2.x} y={point2.y} label="2" color="#ef4444" />
            )}
            {/* 裁切区域 */}
            {point1 && point2 && (
              <CropSelectionBox
                point1={point1}
                point2={point2}
                imageDimensions={imageDimensions}
                displayedImageSize={displayedImageSize}
              />
            )}
          </div>
        </div>
      </div>

      {/* 操作面板 */}
      <div className="rounded-lg bg-white p-4 shadow-lg dark:bg-slate-800">
        <div className="mb-4 space-y-4">
          {/* 第一个点 */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
              第一个点 (x1, y1):
            </span>
            {point1 ? (
              <>
                <input
                  type="number"
                  className="w-24 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  value={getPixelsCoordinates(point1).x}
                  onChange={(e) => {
                    const x = parseInt(e.target.value) || 0;
                    const clampedX = Math.max(0, Math.min(x, imageDimensions.width - 1));
                    const displayX = clampedX / (imageDimensions.width / displayedImageSize.width);
                    setPoint1({ ...point1, x: displayX });
                  }}
                />
                <input
                  type="number"
                  className="w-24 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  value={getPixelsCoordinates(point1).y}
                  onChange={(e) => {
                    const y = parseInt(e.target.value) || 0;
                    const clampedY = Math.max(0, Math.min(y, imageDimensions.height - 1));
                    const displayY = clampedY / (imageDimensions.height / displayedImageSize.height);
                    setPoint1({ ...point1, y: displayY });
                  }}
                />
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  (范围: 0-{imageDimensions.width - 1}, 0-{imageDimensions.height - 1})
                </span>
              </>
            ) : (
              <span className="rounded bg-slate-100 px-3 py-1 text-sm font-mono dark:bg-slate-700 dark:text-slate-200">
                未选择
              </span>
            )}
          </div>

          {/* 第二个点 */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
              第二个点 (x2, y2):
            </span>
            {point2 ? (
              <>
                <input
                  type="number"
                  className="w-24 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  value={getPixelsCoordinates(point2).x}
                  onChange={(e) => {
                    const x = parseInt(e.target.value) || 0;
                    const clampedX = Math.max(0, Math.min(x, imageDimensions.width - 1));
                    const displayX = clampedX / (imageDimensions.width / displayedImageSize.width);
                    setPoint2({ ...point2, x: displayX });
                  }}
                />
                <input
                  type="number"
                  className="w-24 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  value={getPixelsCoordinates(point2).y}
                  onChange={(e) => {
                    const y = parseInt(e.target.value) || 0;
                    const clampedY = Math.max(0, Math.min(y, imageDimensions.height - 1));
                    const displayY = clampedY / (imageDimensions.height / displayedImageSize.height);
                    setPoint2({ ...point2, y: displayY });
                  }}
                />
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  (范围: 0-{imageDimensions.width - 1}, 0-{imageDimensions.height - 1})
                </span>
              </>
            ) : (
              <span className="rounded bg-slate-100 px-3 py-1 text-sm font-mono dark:bg-slate-700 dark:text-slate-200">
                未选择
              </span>
            )}
          </div>

          {/* 裁切区域信息 */}
          {cropArea && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                裁切区域:
              </span>
              <span className="rounded bg-blue-100 px-3 py-1 text-sm font-mono text-blue-700 dark:bg-blue-900 dark:text-blue-200">
                {cropArea.width} × {cropArea.height}
              </span>
              <span className="rounded bg-green-100 px-3 py-1 text-sm font-mono text-green-700 dark:bg-green-900 dark:text-green-200">
                面积: {formatArea(pixelArea)}
              </span>
            </div>
          )}
        </div>

        {/* 按钮区 */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleReset}
            className="rounded-lg bg-slate-600 px-4 py-2 text-white transition-colors hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600"
            disabled={!point1}
          >
            重置
          </button>
          <button
            onClick={handleSaveAndDownload}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400 dark:bg-blue-700 dark:hover:bg-blue-600 dark:disabled:bg-slate-600"
            disabled={!point1 || !point2}
          >
            保存并下载
          </button>
        </div>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          说明：点击图片选择第一个标记点，再点击选择第二个标记点。也可以手动输入坐标精确调整。裁切区域为两个点围成的矩形。
        </p>
      </div>
    </div>
  );
}

function gettoString(point: { x: number; y: number }) {
  return `${point.x}, ${point.y}`;
}

function MarkerPoint({
  x,
  y,
  label,
  color,
}: {
  x: number;
  y: number;
  label: string;
  color: string;
}) {
  if (isNaN(x) || isNaN(y)) return null;

  return (
    <div
      className="absolute -ml-3 -mt-3 h-6 w-6 flex items-center justify-center rounded-full text-xs font-bold text-white shadow-lg cursor-pointer"
      style={{ left: x, top: y, backgroundColor: color }}
    >
      {label}
    </div>
  );
}

function CropSelectionBox({
  point1,
  point2,
  imageDimensions,
  displayedImageSize,
}: {
  point1: { x: number; y: number };
  point2: { x: number; y: number };
  imageDimensions: { width: number; height: number };
  displayedImageSize: { width: number; height: number };
}) {
  const x = Math.min(point1.x, point2.x);
  const y = Math.min(point1.y, point2.y);
  const width = Math.abs(point2.x - point1.x);
  const height = Math.abs(point2.y - point1.y);

  if (isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height) ||
      displayedImageSize.width === 0 || displayedImageSize.height === 0) {
    return null;
  }

  const scaleX = imageDimensions.width / displayedImageSize.width;
  const scaleY = imageDimensions.height / displayedImageSize.height;

  const pixelWidth = Math.round(width * scaleX);
  const pixelHeight = Math.round(height * scaleY);

  return (
    <div
      className="absolute border-2 border-dashed border-blue-500 bg-blue-500/10"
      style={{ left: x, top: y, width, height }}
    >
      <div className="absolute -top-6 left-0 rounded bg-blue-500 px-2 py-1 text-xs text-white">
        {pixelWidth} × {pixelHeight}
      </div>
    </div>
  );
}

function HorizontalRuler({
  width,
  totalPixels,
}: {
  width: number;
  totalPixels: number;
}) {
  if (!width || !totalPixels) return null;

  const steps = Math.min(10, Math.floor(width / 100));
  const stepSize = width / steps;
  const pixelStep = totalPixels / steps;

  return (
    <div className="relative h-8 border-b border-slate-300 bg-slate-50 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 overflow-visible" style={{ width }}>
      {Array.from({ length: steps + 1 }).map((_, i) => (
        <div
          key={i}
          className="absolute transform -translate-x-1/2 z-10"
          style={{ left: `${i * stepSize}px` }}
        >
          <span className="absolute top-0 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            {i === 0 ? '0' : (i === steps ? totalPixels : Math.round(i * pixelStep))}
          </span>
          <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-px ${i === 0 ? 'h-4 bg-red-500' : 'h-3 bg-slate-400 dark:bg-slate-500'}`}></div>
        </div>
      ))}
    </div>
  );
}

function VerticalRuler({
  height,
  totalPixels,
}: {
  height: number;
  totalPixels: number;
}) {
  if (!height || !totalPixels) return null;

  const steps = Math.min(10, Math.floor(height / 100));
  const stepSize = height / steps;
  const pixelStep = totalPixels / steps;

  return (
    <div className="relative w-12 border-r border-slate-300 bg-slate-50 text-xs dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 overflow-visible" style={{ height }}>
      {Array.from({ length: steps + 1 }).map((_, i) => (
        <div
          key={i}
          className="absolute right-0 transform -translate-y-1/2 z-10"
          style={{ top: `${i * stepSize}px` }}
        >
          <span className="absolute -left-8 top-1/2 transform -translate-y-1/2 whitespace-nowrap">
            {i === 0 ? '0' : (i === steps ? totalPixels : Math.round(i * pixelStep))}
          </span>
          <div className={`absolute right-0 top-1/2 transform -translate-y-1/2 h-px ${i === 0 ? 'w-4 bg-red-500' : 'w-3 bg-slate-400 dark:bg-slate-500'}`}></div>
        </div>
      ))}
    </div>
  );
}
